import { requireElement } from "../../core/utils/dom";
import type { ChartProperty, MapChartModel } from "../../core/types/charts";
import { ChartModelParser } from "./ChartModelParser";

/**
 * Chart JSON builder used by the Viewer page.
 *
 * The legend is shared across the map. Each map feature chart owns its own
 * caption, unit, maximum value, and bar values so different regions/countries
 * can use different scales when needed.
 */
export class ChartBuilderService {
  constructor(private readonly root: HTMLElement) {}

  render(model: MapChartModel = ChartModelParser.createTemplate()): void {
    this.root.innerHTML = `
      <div class="chart-builder">
        <div class="form-grid">
          <label>Legend title <input data-chart-field="legend_title" type="text" placeholder="Legend title shown above the map legend" /></label>
        </div>

        <div class="builder-section">
          <div class="section-title-row">
            <h3>Legends</h3>
            <button data-action="add-legend" type="button">Add legend</button>
          </div>
          <p class="muted">Legends define the bar names and colors used in every popup chart. A value named <code>Population</code> needs a legend named <code>Population</code>.</p>
          <table class="builder-table" data-table="legend">
            <thead><tr><th></th><th>Legend name</th><th>Color</th></tr></thead>
            <tbody></tbody>
          </table>
        </div>

        <div class="builder-section">
          <div class="section-title-row">
            <h3>Feature charts</h3>
            <button data-action="add-chart" type="button">Add feature chart</button>
          </div>
          <p class="muted">Each feature chart is linked to one map feature. <code>Map feature name</code> must match the clicked GeoJSON feature <code>properties.name</code>.</p>
          <table class="builder-table" data-table="chart">
            <thead><tr><th></th><th>Map feature chart</th></tr></thead>
            <tbody></tbody>
          </table>
        </div>
      </div>
    `;

    this.bindStaticActions();
    this.setModel(model);
  }

  setModel(model: MapChartModel): void {
    requireElement<HTMLInputElement>('[data-chart-field="legend_title"]', this.root).value = String(model.legend_title ?? "");

    const legendBody = requireElement<HTMLTableSectionElement>('[data-table="legend"] tbody', this.root);
    const chartBody = requireElement<HTMLTableSectionElement>('[data-table="chart"] tbody', this.root);
    legendBody.replaceChildren();
    chartBody.replaceChildren();

    for (const legend of model.legends ?? []) this.addLegendRow(legend.name, legend.color);
    for (const chart of model.charts ?? []) {
      this.addChartRow(chart.country_name, chart.properties, {
        unit: chart.unit ?? model.unit ?? "",
        chartCaption: chart.chart_caption ?? model.chart_caption ?? "",
        chartMaxValue: chart.chart_max_value ?? model.chart_max_value ?? ""
      });
    }

    if (!legendBody.rows.length) this.addLegendRow();
    if (!chartBody.rows.length) this.addChartRow();
  }

  getModel(): MapChartModel {
    const legendTitle = requireElement<HTMLInputElement>('[data-chart-field="legend_title"]', this.root).value;

    const legends = Array.from(this.root.querySelectorAll<HTMLTableRowElement>('[data-legend-row]')).map((row) => ({
      name: requireElement<HTMLInputElement>('[data-legend-name]', row).value,
      color: requireElement<HTMLInputElement>('[data-legend-color]', row).value
    }));

    const charts = Array.from(this.root.querySelectorAll<HTMLTableRowElement>('[data-chart-row]')).map((row) => {
      const countryName = requireElement<HTMLInputElement>('[data-chart-country-name]', row).value;
      const unit = requireElement<HTMLInputElement>('[data-chart-unit]', row).value;
      const chartCaption = requireElement<HTMLInputElement>('[data-chart-caption]', row).value;
      const chartMaxValue = requireElement<HTMLInputElement>('[data-chart-max-value]', row).value;
      const properties = Array.from(row.querySelectorAll<HTMLTableRowElement>('[data-chart-property-row]')).map((propertyRow) => ({
        name: requireElement<HTMLInputElement>('[data-chart-property-name]', propertyRow).value,
        value: requireElement<HTMLInputElement>('[data-chart-property-value]', propertyRow).value
      }));

      return {
        country_name: countryName,
        unit,
        chart_caption: chartCaption,
        chart_max_value: chartMaxValue,
        properties
      };
    });

    return {
      legend_title: legendTitle,
      legends,
      unit: "",
      chart_caption: "",
      chart_max_value: "",
      charts
    };
  }

  addLegendRow(name = "", color = "#000000"): void {
    const tbody = requireElement<HTMLTableSectionElement>('[data-table="legend"] tbody', this.root);
    const row = document.createElement("tr");
    row.dataset.legendRow = "true";

    const deleteCell = row.insertCell();
    const nameCell = row.insertCell();
    const colorCell = row.insertCell();

    const deleteButton = this.createSmallButton("-", "Remove legend row");
    deleteButton.classList.add("danger");
    deleteButton.addEventListener("click", () => row.remove());
    deleteCell.append(deleteButton);

    const nameInput = document.createElement("input");
    nameInput.dataset.legendName = "true";
    nameInput.type = "text";
    nameInput.placeholder = "Legend name, e.g. Population";
    nameInput.value = name;
    nameCell.append(nameInput);

    const colorInput = document.createElement("input");
    colorInput.dataset.legendColor = "true";
    colorInput.type = "color";
    colorInput.value = this.normalizeColor(color);
    colorCell.append(colorInput);

    tbody.append(row);
  }

  addChartRow(countryName = "", properties: ChartProperty[] = [], options: { unit?: string; chartCaption?: string; chartMaxValue?: string | number } = {}): void {
    const tbody = requireElement<HTMLTableSectionElement>('[data-table="chart"] tbody', this.root);
    const row = document.createElement("tr");
    row.dataset.chartRow = "true";

    const deleteCell = row.insertCell();
    const bodyCell = row.insertCell();

    const deleteButton = this.createSmallButton("-", "Remove feature chart");
    deleteButton.classList.add("danger");
    deleteButton.addEventListener("click", () => row.remove());
    deleteCell.append(deleteButton);

    const wrapper = document.createElement("div");
    wrapper.className = "feature-chart-card";
    wrapper.innerHTML = `
      <div class="form-grid">
        <label>Map feature name <input data-chart-country-name="true" type="text" placeholder="Must match GeoJSON properties.name, e.g. Misr" /></label>
        <label>Chart unit <input data-chart-unit="true" type="text" placeholder="%, km, people, etc." /></label>
        <label>Chart caption <input data-chart-caption="true" type="text" placeholder="Popup chart title for this feature" /></label>
        <label>Chart max value <input data-chart-max-value="true" type="number" min="0" step="any" placeholder="100" /></label>
      </div>
      <p class="muted small-text">This unit, caption, and max value apply only to this map feature chart. Use this when countries or regions need different scales.</p>
    `;

    requireElement<HTMLInputElement>('[data-chart-country-name]', wrapper).value = countryName;
    requireElement<HTMLInputElement>('[data-chart-unit]', wrapper).value = String(options.unit ?? "");
    requireElement<HTMLInputElement>('[data-chart-caption]', wrapper).value = String(options.chartCaption ?? "");
    requireElement<HTMLInputElement>('[data-chart-max-value]', wrapper).value = String(options.chartMaxValue ?? "");

    const nestedTable = document.createElement("table");
    nestedTable.className = "builder-table nested-table";
    nestedTable.innerHTML = `
      <thead><tr><th></th><th>Value name / legend name</th><th>Value</th></tr></thead>
      <tbody></tbody>
      <tfoot><tr><td colspan="3"></td></tr></tfoot>
    `;

    const addPropertyButton = this.createSmallButton("Add value", "Add chart value");
    addPropertyButton.addEventListener("click", () => this.addChartPropertyRow(nestedTable, "", ""));
    requireElement<HTMLTableCellElement>("tfoot td", nestedTable).append(addPropertyButton);

    wrapper.append(nestedTable);
    bodyCell.append(wrapper);
    tbody.append(row);

    if (properties.length) {
      for (const property of properties) this.addChartPropertyRow(nestedTable, property.name, property.value);
    } else {
      this.addChartPropertyRow(nestedTable, "", "");
    }
  }

  private addChartPropertyRow(table: HTMLTableElement, name = "", value: string | number = ""): void {
    const tbody = requireElement<HTMLTableSectionElement>("tbody", table);
    const row = document.createElement("tr");
    row.dataset.chartPropertyRow = "true";

    const deleteCell = row.insertCell();
    const nameCell = row.insertCell();
    const valueCell = row.insertCell();

    const deleteButton = this.createSmallButton("-", "Remove chart value");
    deleteButton.classList.add("danger");
    deleteButton.addEventListener("click", () => row.remove());
    deleteCell.append(deleteButton);

    const nameInput = document.createElement("input");
    nameInput.dataset.chartPropertyName = "true";
    nameInput.type = "text";
    nameInput.placeholder = "Value name, e.g. Population";
    nameInput.value = name;
    nameCell.append(nameInput);

    const valueInput = document.createElement("input");
    valueInput.dataset.chartPropertyValue = "true";
    valueInput.type = "number";
    valueInput.min = "0";
    valueInput.step = "any";
    valueInput.placeholder = "Value, e.g. 100";
    valueInput.value = String(value);
    valueCell.append(valueInput);

    tbody.append(row);
  }

  private bindStaticActions(): void {
    requireElement<HTMLButtonElement>('[data-action="add-legend"]', this.root).addEventListener("click", () => this.addLegendRow());
    requireElement<HTMLButtonElement>('[data-action="add-chart"]', this.root).addEventListener("click", () => this.addChartRow());
  }

  private createSmallButton(text: string, title: string): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "small-button";
    button.textContent = text;
    button.title = title;
    return button;
  }

  private normalizeColor(value: string): string {
    return /^#[0-9a-f]{6}$/i.test(value) ? value : "#000000";
  }
}
